"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { SessionContext } from "next-auth/react";
import type { Session } from "next-auth";
import { CartProvider } from "@/lib/hooks/CartProvider";
import { redactSensitiveData } from "@/lib/utils/redactSensitiveData";
import { AbilityProvider } from "@/components/providers/AbilityProvider";
import { GoogleAnalyticsProvider } from "@/components/providers/GoogleAnalyticsProvider";
import { handleForceSignOut } from "@/lib/auth/authSessionHandler";
import {
  setSessionCache,
  getCachedSession,
  refreshSessionNow,
  PROACTIVE_REFRESH_MARGIN_MS,
} from "@/lib/auth/sessionCache";
import type { ClientErrorContext } from "@/lib/models/clientLog";

// Espera mínima antes de reintentar cuando un refresh no avanzó el jwtExp
// (p.ej. SECURITY_REFRESH_TOKEN_REUSED transitorio en authOptions.ts, que
// devuelve el token sin cambios) - evita reprogramar casi a 0ms en bucle
// mientras esa condición transitoria se resuelve por sí sola.
const MIN_RETRY_DELAY_MS = 10 * 1000;

// Jitter pequeño y aleatorio sobre el disparo del temporizador: evita que
// muchas sesiones cuyo jwtExp coincida por casualidad (ej. varios usuarios
// que iniciaron sesión casi a la misma hora) golpeen el backend en el mismo
// instante exacto.
const SCHEDULE_JITTER_MAX_MS = 15 * 1000;

// Con varias pestañas del mismo usuario abiertas, cada una calcula el mismo
// jwtExp (viene de la misma cookie) y dispararía su propio refresh casi al
// mismo instante - multiplicando peticiones sin necesidad. Web Locks API
// coordina esto entre pestañas del mismo origen: solo una pestaña ejecuta el
// refresh real a la vez: las demás esperan su turno y, al tomarlo, ya
// encuentran la cookie actualizada por la que ganó - el callback jwt() de
// authOptions.ts ve que ya no hace falta refrescar y no dispara una segunda
// petición real al backend. Navegadores sin soporte (muy raros hoy) caen al
// camino directo, sin coordinación entre pestañas pero sin romper nada.
const SESSION_REFRESH_LOCK_NAME = "adises-proactive-session-refresh";

async function refreshSessionWithCrossTabLock() {
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return navigator.locks.request(SESSION_REFRESH_LOCK_NAME, () =>
      refreshSessionNow(),
    );
  }
  return refreshSessionNow();
}

interface ProvidersProps {
  children: React.ReactNode;
  session?: Session | null;
}

export function Providers({ children, session }: ProvidersProps) {
  const [currentSession, setCurrentSession] = useState<Session | null | undefined>(session);

  useEffect(() => {
    if (session !== undefined) {
      setSessionCache(session);
      setCurrentSession(session);
    }
  }, [session]);

  // Refresh proactivo: en vez de esperar a que una petición cualquiera
  // descubra que el token venció (reactivo), se programa un único temporizador
  // - por pestaña - que dispara el refresh un poco antes de que venza,
  // usando el jwtExp real. Al completarse, se reprograma con el jwtExp nuevo,
  // así se auto-perpetúa mientras la pestaña siga abierta y con sesión.
  const updateSession = useCallback(
    async (data?: unknown): Promise<Session | null> => {
      try {
        const res = await fetch("/api/auth/session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data ?? {}),
        });
        if (res.ok) {
          const newSession: Session = await res.json();
          setCurrentSession(newSession);
          setSessionCache(newSession);
          return newSession;
        }
      } catch {
        // En caso de fallo en el POST, reintentar con refresh directo
      }
      const fresh = await refreshSessionNow();
      setCurrentSession(fresh);
      return fresh;
    },
    [],
  );

  // Refresh proactivo: en vez de esperar a que una petición cualquiera
  // descubra que el token venció (reactivo), se programa un único temporizador
  // - por pestaña - que dispara el refresh un poco antes de que venza,
  // usando el jwtExp real. Al completarse, se reprograma con el jwtExp nuevo,
  // así se auto-perpetúa mientras la pestaña siga abierta y con sesión.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!currentSession?.user?.jwtExp) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function scheduleFrom(jwtExp: number, previousJwtExp?: number) {
      if (cancelled) return;

      const noProgress =
        previousJwtExp !== undefined && jwtExp <= previousJwtExp;
      const rawDelay = jwtExp * 1000 - PROACTIVE_REFRESH_MARGIN_MS - Date.now();
      const baseDelay = noProgress
        ? Math.max(rawDelay, MIN_RETRY_DELAY_MS)
        : Math.max(rawDelay, 0);
      const delay = baseDelay + Math.floor(Math.random() * SCHEDULE_JITTER_MAX_MS);

      fetch("/api/logs", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          redactSensitiveData({
            keyError: "PROACTIVE_REFRESH_SCHEDULED",
            delayMs: delay,
            noProgress,
            targetJwtExp: jwtExp,
            usuario: currentSession?.user?.email,
            accountID: currentSession?.user?.tokenDecode?.accountId,
          }),
        ),
      }).catch(() => {});

      timer = setTimeout(async () => {
        if (cancelled) return;
        const refreshed = await refreshSessionWithCrossTabLock();
        if (cancelled) return;

        const outcome = !refreshed?.user?.accessToken
          ? "stopped_no_session"
          : refreshed.user.error === "RefreshAccessTokenError"
            ? "stopped_dead"
            : "success";

        fetch("/api/logs", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            redactSensitiveData({
              keyError: "PROACTIVE_REFRESH_RESULT",
              outcome,
              usuario: refreshed?.user?.email ?? currentSession?.user?.email,
              accountID: refreshed?.user?.tokenDecode?.accountId,
            }),
          ),
        }).catch(() => {});

        // Sesión cerrada o refresh realmente muerto (no transitorio): no
        // tiene sentido seguir reprogramando y se procede a cerrar sesión.
        if (refreshed?.user?.error === "RefreshAccessTokenError") {
          handleForceSignOut("REFRESH_ACCESS_TOKEN_ERROR");
          return;
        }

        if (!refreshed?.user?.accessToken) {
          return;
        }

        scheduleFrom(refreshed.user.jwtExp, jwtExp);
      }, delay);
    }

    scheduleFrom(currentSession.user.jwtExp);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [
    currentSession?.user?.jwtExp,
    currentSession?.user?.email,
    currentSession?.user?.tokenDecode?.accountId,
  ]);

  useEffect(() => {
    if (currentSession?.user?.error === "RefreshAccessTokenError") {
      handleForceSignOut("REFRESH_ACCESS_TOKEN_ERROR");
    }
  }, [currentSession?.user?.error]);

  const sessionContextValue = useMemo(() => {
    if (currentSession?.user) {
      return {
        data: currentSession,
        status: "authenticated" as const,
        update: updateSession,
      };
    }
    return {
      data: null,
      status: "unauthenticated" as const,
      update: updateSession,
    };
  }, [currentSession, updateSession]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const showDebug =
      process.env.NEXT_PUBLIC_SHOW_DEBUG === "true" ||
      (process.env.NEXT_PUBLIC_SHOW_DEBUG === undefined &&
        process.env.NODE_ENV !== "production");
    const showInfo =
      process.env.NEXT_PUBLIC_SHOW_INFO === "true" ||
      (process.env.NEXT_PUBLIC_SHOW_INFO === undefined &&
        process.env.NODE_ENV !== "production");
    const showWarn = process.env.NEXT_PUBLIC_SHOW_WARN !== "false";
    const showError = process.env.NEXT_PUBLIC_SHOW_ERROR !== "false";

    if (!showDebug) {
      console.debug = () => {};
    }
    if (!showInfo) {
      console.log = () => {};
      console.info = () => {};
    }
    if (!showWarn) {
      console.warn = () => {};
    }
    if (!showError) {
      console.error = () => {};
    }

    const handleClientError = async (
      message: string,
      stack: string | undefined,
      errorDetails?: unknown,
      extraContext?: ClientErrorContext,
    ) => {
      try {
        const currentSession = await getCachedSession();
        const usuario =
          currentSession?.user?.user?.email ||
          currentSession?.user?.email ||
          "Desconocido";
        const loosUser = currentSession?.user as
          | Record<string, unknown>
          | undefined;
        const accountID =
          currentSession?.user?.tokenDecode?.accountId ||
          (loosUser?.accountId as string | undefined) ||
          "N/A";
        const userID =
          currentSession?.user?.id ||
          (loosUser?.user as Record<string, unknown> | undefined)?.id ||
          currentSession?.user?.tokenDecode?.sub ||
          "N/A";

        const getSafeBody = () => {
          const cache = new Set();
          return JSON.stringify(
            redactSensitiveData({
              keyError: "FRONT_ERROR",
              errorMsg: message,
              stack: stack || "No stack trace available",
              url: extraContext?.endpointUrl || window.location.href,
              usuario,
              accountID,
              userID,
              method: extraContext?.method,
              status: extraContext?.status,
              statusText: extraContext?.statusText,
              errorCode: extraContext?.errorCode,
              traceId: extraContext?.traceId,
              backendResponse: extraContext?.backendResponse,
              ...(errorDetails ? { errorDetails } : {}),
            }),
            (_key, value) => {
              if (typeof value === "object" && value !== null) {
                if (cache.has(value)) {
                  return "[Circular Reference]";
                }
                cache.add(value);
              }
              return value;
            },
          );
        };

        fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: getSafeBody(),
        }).catch(() => {});
      } catch {
        // Ignorar errores del logger para evitar recursión
      }
    };

    const onError = (event: ErrorEvent) => {
      if (event.filename && event.filename.includes("/api/logs")) return;
      handleClientError(event.message, event.error?.stack);
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      let message = "Unhandled Promise Rejection";
      let stack: string | undefined = undefined;
      let errorDetails: unknown = null;
      let extraContext: ClientErrorContext = {};

      if (reason instanceof Error) {
        message = `Unhandled Promise Rejection: ${reason.message}`;
        stack = reason.stack;
      } else if (reason !== null && reason !== undefined) {
        if (typeof reason === "object") {
          errorDetails = reason;
          const obj = reason as Record<string, unknown>;

          const resp =
            (obj.response && typeof obj.response === "object"
              ? (obj.response as Record<string, unknown>)
              : null) ||
            (obj.data && typeof obj.data === "object"
              ? (obj.data as Record<string, unknown>)
              : null);
          const respData =
            (resp?.data && typeof resp.data === "object"
              ? (resp.data as Record<string, unknown>)
              : null) ||
            (typeof obj.data === "object"
              ? (obj.data as Record<string, unknown>)
              : null);

          const status =
            (typeof obj.status === "number" || typeof obj.status === "string"
              ? obj.status
              : undefined) ||
            (typeof resp?.status === "number" || typeof resp?.status === "string"
              ? resp.status
              : undefined);
          const statusText =
            typeof obj.statusText === "string"
              ? obj.statusText
              : typeof resp?.statusText === "string"
                ? resp.statusText
                : undefined;
          const errorCode =
            typeof obj.code === "string"
              ? obj.code
              : typeof respData?.code === "string"
                ? (respData.code as string)
                : undefined;
          const traceId =
            typeof obj.traceId === "string"
              ? obj.traceId
              : typeof respData?.traceId === "string"
                ? (respData.traceId as string)
                : undefined;

          const config =
            obj.config && typeof obj.config === "object"
              ? (obj.config as Record<string, unknown>)
              : null;
          const method =
            typeof obj.method === "string"
              ? obj.method
              : typeof config?.method === "string"
                ? (config.method as string)
                : undefined;
          const endpointUrl =
            typeof obj.url === "string"
              ? obj.url
              : typeof config?.url === "string"
                ? (config.url as string)
                : undefined;

          const detail =
            typeof obj.detail === "string"
              ? obj.detail
              : typeof obj.message === "string"
                ? obj.message
                : typeof obj.title === "string"
                  ? obj.title
                  : typeof respData?.detail === "string"
                    ? (respData.detail as string)
                    : typeof respData?.message === "string"
                      ? (respData.message as string)
                      : typeof respData?.title === "string"
                        ? (respData.title as string)
                        : undefined;

          if (typeof obj.stack === "string") {
            stack = obj.stack;
          }

          extraContext = {
            status,
            statusText,
            errorCode,
            traceId,
            method,
            endpointUrl,
            backendResponse: respData || resp || obj,
          };

          const parts = [
            status ? `[${status}]` : "",
            method ? method.toUpperCase() : "",
            endpointUrl ? endpointUrl : "",
            errorCode ? `[${errorCode}]` : "",
            detail || "",
          ].filter(Boolean);

          if (parts.length > 0) {
            message = `Unhandled Promise Rejection: ${parts.join(" ")}`;
          } else {
            try {
              message = `Unhandled Promise Rejection: ${JSON.stringify(reason).slice(0, 300)}`;
            } catch {
              message = "Unhandled Promise Rejection (Object)";
            }
          }
        } else {
          message = `Unhandled Promise Rejection: ${String(reason)}`;
        }
      }

      handleClientError(message, stack, errorDetails, extraContext);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <AbilityProvider>
        <CartProvider>
          <GoogleAnalyticsProvider>{children}</GoogleAnalyticsProvider>
        </CartProvider>
      </AbilityProvider>
    </SessionContext.Provider>
  );
}
